import axios from 'axios'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const diagnosticCss = `
  /* ── Modal backdrop ── */
  .diag-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    animation: backdropIn .25s ease;
  }

  @keyframes backdropIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* ── Modal sheet ── */
  .diag-sheet {
    width: 100%;
    max-width: 520px;
    background: #ffffff;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -8px 40px rgba(168, 41, 93, 0.14);
    font-family: 'DM Sans', sans-serif;
    animation: sheetUp .32s cubic-bezier(0.22,1,0.36,1);
    max-height: 88vh;
    display: flex;
    position: relative;
    flex-direction: column;
    overflow: hidden;
    top: -80px;
  }

  @keyframes sheetUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  /* ── Handle bar ── */
  .diag-handle-wrap {
    display: flex;
    justify-content: center;
    padding: 12px 0 4px;
    flex-shrink: 0;
  }

  .diag-handle {
    width: 36px; height: 4px;
    border-radius: 99px;
    background: #d4a5a5;
  }

  /* ── Header inside modal ── */
  .diag-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 20px 14px;
    border-bottom: 1px solid rgba(212, 165, 165, 0.25);
    flex-shrink: 0;
  }

  .diag-modal-title {
    font-size: 16px;
    font-weight: 700;
    color: #000000;
    letter-spacing: -0.2px;
  }

  .diag-close-btn {
    width: 30px; height: 30px;
    border-radius: 50%;
    border: none;
    background: #fdf0f4;
    color: #a8295d;
    font-size: 15px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background .15s;
  }

  .diag-close-btn:hover { background: #f9dde7; }

  /* ── Scrollable body ── */
  .diag-body {
    padding: 18px 20px 24px;
    overflow-y: auto;
    flex: 1;
  }

  .diag-body::-webkit-scrollbar { width: 4px; }
  .diag-body::-webkit-scrollbar-thumb { background: #d4a5a5; border-radius: 99px; }

  /* ── Section ── */
  .diag-section {
    margin-bottom: 16px;
  }

  .diag-label {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #d4a5a5;
    margin-bottom: 6px;
  }

  .diag-box {
    background: #fdf8f9;
    border-radius: 12px;
    padding: 12px 14px;
    font-size: 13.5px;
    color: #000000;
    line-height: 1.65;
    border: 1px solid rgba(212, 165, 165, 0.25);
  }

  .diag-box.diag-issue {
    background: #fdf0f4;
    border-color: rgba(168, 41, 93, 0.22);
    font-weight: 600;
    color: #a8295d;
  }

  /* ── LLM output loading ── */
  .diag-llm-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    color: #d4a5a5;
    font-size: 13px;
  }

  .diag-llm-spinner {
    width: 18px; height: 18px;
    border: 2px solid rgba(212, 165, 165, 0.25);
    border-top-color: #a8295d;
    border-radius: 50%;
    animation: spin .8s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Share section ── */
  .diag-share {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid rgba(212, 165, 165, 0.25);
  }

  .diag-share-title {
    font-size: 13px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 3px;
  }

  .diag-share-sub {
    font-size: 11.5px;
    color: #d4a5a5;
    margin-bottom: 10px;
  }

  .diag-icons {
    display: flex;
    gap: 10px;
  }

  .diag-icon {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: #fdf0f4;
    border: 1px solid rgba(212, 165, 165, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    cursor: pointer;
    transition: transform .15s, background .15s;
  }

  .diag-icon:hover { transform: scale(1.1); background: #f9dde7; }

  /* ── Trigger button ── */
  .diag-trigger-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #000000;
    color: #ffffff;
    border: none;
    padding: 11px 20px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: opacity .15s, transform .1s;
    margin-top: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .diag-trigger-btn:hover { opacity: .88; }
  .diag-trigger-btn:active { transform: scale(.97); }

  .diag-trigger-btn .diag-trigger-icon {
    font-size: 15px;
  }

  /* ── Internal view diagnostics button ── */
  .diag-view-btn {
    width: 100%;
    padding: 12px;
    border-radius: 12px;
    border: none;
    background: #a8295d;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    margin-top: 16px;
    transition: opacity .15s, transform .1s, box-shadow .15s;
    box-shadow: 0 4px 14px rgba(168, 41, 93, 0.28);
  }

  .diag-view-btn:hover:not(:disabled) { opacity: .88; box-shadow: 0 6px 18px rgba(168, 41, 93, 0.38); }
  .diag-view-btn:active:not(:disabled) { transform: scale(.98); }
  .diag-view-btn:disabled { opacity: .4; cursor: not-allowed; }
`

/**
 * DiagnosticCard
 *
 * Props:
 *   isOpen            boolean   — whether modal is visible
 *   onClose           function  — close the modal
 *   productsReady     boolean   — products must be loaded before diagnostics API can be called
 */
export default function DiagnosticCard({
  isOpen,
  onClose,
  diagnosticRequestData,
  productsReady = false,
  routineText = '',
  showRoutineAdvice = false,
}) {
  const dispatch = useDispatch()
  // const contraindications = useSelector((state) => state.contraindications)
  const diagnosticsLLMText = useSelector((state) => state.diagnosticsLLMText)

  const [loadingDiag, setLoadingDiag] = useState(false)
  const [diagError, setDiagError] = useState(null)
  const [diagFetched, setDiagFetched] = useState(false)

  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}
  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  const modalTitle = showRoutineAdvice
    ? `🌿 ${translateText('chat.diagnostics.routineAdvice')}`
    : `🔬 ${translateText('chat.diagnostics.title')}`

  const generateDiagnosticPrompt = () => {
    if (!productsReady || !diagnosticRequestData) return

    setLoadingDiag(true)
    setDiagError(null)
    const normalizedApiLanguage = String(
      language || 'EN',
    )
      .trim()
      .toLowerCase()

    const apiLanguage = [
      'en',
      'fr',
      'nl',
      'sw',
    ].includes(normalizedApiLanguage)
      ? normalizedApiLanguage
      : 'en'
    axios
      .post(
        'https://hal-cos-prd-gwc-fnapp-v1.azurewebsites.net/api/creatediagnosticprompt',
        {
          language: apiLanguage,

          answers:
            diagnosticRequestData?.answers,

          contraindications:
            diagnosticRequestData?.constraints,

          encoded_category: 'None',
          env: 'Prd',
          is_trial: 'True',
        },
        { headers: { 'Content-Type': 'application/json; charset=utf-8' } },
      )
      .then(({ data: resp }) => {
        if (resp?.llm_output) {
          dispatch({ type: 'DIAGNOSTICS_LLM_TEXT', payload: resp.llm_output })
          setDiagFetched(true)
        } else {
          setDiagError('No diagnostics returned. Please try again.')
        }
      })
      .catch(() => {
        setDiagError("Couldn't load diagnostics right now. Please try again.")
      })
      .finally(() => setLoadingDiag(false))
  }

  if (!isOpen) return null

  return (
    <>
      <style>{diagnosticCss}</style>

      {/* Backdrop */}
      <div className='diag-backdrop' onClick={onClose}>
        {/* Sheet — stop click propagating to backdrop */}
        <div className='diag-sheet' onClick={(e) => e.stopPropagation()}>
          {/* Handle */}
          <div className='diag-handle-wrap'>
            <div className='diag-handle' />
          </div>

          {/* Header */}
          <div className='diag-modal-header'>
            <div className='diag-modal-title'>{modalTitle}</div>
            <button className='diag-close-btn' onClick={onClose}>
              ✕
            </button>
          </div>

          {/* Body */}
          <div className='diag-body'>
            {/* Static sections */}

            {/* AI Diagnostics section */}
            <div className='diag-section'>
              <div className='diag-label'>
                {translateText('chat.diagnostics.aiDiagnostics')}
              </div>

              {/* Not yet fetched */}
              {!diagFetched && !loadingDiag && (
                <button
                  className='diag-view-btn'
                  onClick={generateDiagnosticPrompt}
                  disabled={!productsReady}
                  title={
                    !productsReady
                      ? translateText('chat.diagnostics.waiting')
                      : ''
                  }
                >
                  {productsReady
                    ? translateText('chat.diagnostics.generate')
                    : translateText('chat.diagnostics.waiting')}
                </button>
              )}

              {/* Loading */}
              {loadingDiag && (
                <div className='diag-llm-loading'>
                  <div className='diag-llm-spinner' />
                  <span>{translateText('chat.diagnostics.analysing')}</span>
                </div>
              )}

              {/* Error */}
              {diagError && !loadingDiag && (
                <div
                  style={{
                    color: '#c05050',
                    fontSize: '12.5px',
                    marginTop: '8px',
                  }}
                >
                  {diagError}
                  <button
                    onClick={generateDiagnosticPrompt}
                    style={{
                      marginLeft: '8px',
                      color: '#c4a064',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '12.5px',
                    }}
                  >
                    {translateText('chat.diagnostics.retry')}
                  </button>
                </div>
              )}

              {/* Result */}
              {diagFetched && diagnosticsLLMText && !loadingDiag && (
                <>
                  <div
                    className='diag-box'
                    style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }}
                  >
                    {diagnosticsLLMText}
                  </div>
                </>
              )}
            </div>
            {showRoutineAdvice && routineText && (
              <div className='diag-section'>
                <div className='diag-label'>
                  {translateText('chat.diagnostics.routineAdvice')}
                </div>
                <div
                  className='diag-box'
                  style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }}
                >
                  {routineText}
                </div>
              </div>
            )}

            {/* Share */}
            <div className='diag-share'>
              <div className='diag-share-title'>
                {translateText('chat.diagnostics.shareTitle')}
              </div>
              <div className='diag-share-sub'>
                {translateText('chat.diagnostics.shareSub')}
              </div>
              <div className='diag-icons'>
                <div className='diag-icon'>f</div>
                <div className='diag-icon'>𝕏</div>
                <div className='diag-icon'>📸</div>
                <div className='diag-icon'>🔗</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
